/**
 * The pairing payload carried by the QR / copy-paste key that `npm run pair`
 * shows and the phone scans. Encoded compactly as `cc1:<base64url(json)>` so the
 * QR stays small enough to scan as terminal block-art. The phone accepts either
 * this compact form or raw JSON (see PhoneEngine.addPairing).
 *
 * It contains the `pairingSecret` — this string is the E2E content key and is
 * shared MCP-server → phone over a LOCAL channel only; it never touches the
 * backend.
 *
 * PLATFORM-NEUTRAL (btoa/atob + TextEncoder, present in Node 20+ and browsers) so this exact file is
 * imported by BOTH the MCP server and the web client (firebase/web via the `@shared` alias) — no port,
 * no drift.
 */
import { utf8ToBytes, bytesToUtf8, bytesToB64url, b64urlToBytes } from "./encoding.js";

export interface PairingPayload {
  name: string;
  pairingId: string;
  pairingSecret: string;
  deviceRegToken: string;
  functionsBaseUrl: string;
}

export function encodePairingPayload(p: PairingPayload): string {
  return `cc1:${bytesToB64url(utf8ToBytes(JSON.stringify(p)))}`;
}

export function decodePairingPayload(s: string): PairingPayload | undefined {
  try {
    // A deep link (https://…/pair#cc1:…) carries the key in the fragment.
    let t = s.trim();
    if (t.includes("#")) t = t.slice(t.lastIndexOf("#") + 1);
    const raw = t.startsWith("cc1:") ? bytesToUtf8(b64urlToBytes(t.slice(4))) : t; // tolerate raw JSON too
    const o = JSON.parse(raw) as Partial<PairingPayload>;
    if (o.pairingId && o.pairingSecret && o.deviceRegToken && o.functionsBaseUrl) {
      return {
        name: o.name ?? "Claude",
        pairingId: o.pairingId,
        pairingSecret: o.pairingSecret,
        deviceRegToken: o.deviceRegToken,
        functionsBaseUrl: o.functionsBaseUrl,
      };
    }
  } catch {
    /* fall through */
  }
  return undefined;
}
